import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser, sendRegisterOtp, clearError, clearMessage } from '../store/authSlice';
import { useNavigate } from 'react-router-dom';
import { OTP_CONFIG, DEFAULTS, API_URL } from '../config';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { formatPhoneForAPI } from '../utils/phoneUtils';
import { Country, State, City } from 'country-state-city';
import 'react-phone-number-input/style.css';
import './PhoneInput.css';

const Register = () => {
  // Step 1: Phone and OTP
  const [phoneNumber, setPhoneNumber] = useState(() => {
    // Check if phone number was passed from login page
    const loginPhone = localStorage.getItem('loginPhoneForRegistration');
    const savedPhone = localStorage.getItem('registrationPhone');
    
    if (loginPhone) {
      return loginPhone;
    }
    
    return savedPhone || ''; // Empty by default
  });
  const [otp, setOtp] = useState('');
  const [phoneFromLogin, setPhoneFromLogin] = useState(false);
  const [otpSent, setOtpSent] = useState(
    localStorage.getItem('registrationOtpSent') === 'true'
  );
  const [otpVerified, setOtpVerified] = useState(
    localStorage.getItem('registrationOtpVerified') === 'true'
  );
  const [otpTimer, setOtpTimer] = useState(
    parseInt(localStorage.getItem('registrationOtpTimer') || '0')
  );
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // Step 2: Registration Form
  const [formData, setFormData] = useState(() => {
    const savedFormData = localStorage.getItem('registrationFormData');
    return savedFormData ? JSON.parse(savedFormData) : {
      firstName: '',
      lastName: '',
      email: '',
      ownerName: '',
      companyName: '',
      businessIndustry: '',
      city: '',
      state: '',
      country: 'IN' // Default to India
    };
  });

  // Location data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, message, isAuthenticated, otpLoading } = useSelector((state) => state.auth);

  // Save registration state to localStorage
  useEffect(() => {
    localStorage.setItem('registrationPhone', phoneNumber);
  }, [phoneNumber]);

  // Prevent direct access to registration form without OTP verification
  useEffect(() => {
    // If user navigated directly to register page without OTP verification
    const hasValidSession = otpSent || otpVerified || localStorage.getItem('registrationOtpSent') === 'true';
    
    if (!hasValidSession && !phoneNumber) {
      // Reset all states to ensure user starts from phone input
      setOtpSent(false);
      setOtpVerified(false);
      setOtpTimer(0);
      setOtp('');
    }
  }, [otpSent, otpVerified, phoneNumber]);

  useEffect(() => {
    localStorage.setItem('registrationOtpSent', otpSent.toString());
  }, [otpSent]);

  useEffect(() => {
    localStorage.setItem('registrationOtpVerified', otpVerified.toString());
  }, [otpVerified]);

  useEffect(() => {
    localStorage.setItem('registrationOtpTimer', otpTimer.toString());
  }, [otpTimer]);

  useEffect(() => {
    localStorage.setItem('registrationFormData', JSON.stringify(formData));
  }, [formData]);

  // Check for existing registration session on component mount
  useEffect(() => {
    const savedPhone = localStorage.getItem('registrationPhone');
    const loginPhone = localStorage.getItem('loginPhoneForRegistration');
    const savedOtpTimer = parseInt(localStorage.getItem('registrationOtpTimer') || '0');
    const savedTimestamp = parseInt(localStorage.getItem('registrationOtpTimestamp') || '0');
    const currentTime = Math.floor(Date.now() / 1000);
    
    // If phone number came from login page, use it and clear the storage
    if (loginPhone && !phoneNumber) {
      setPhoneNumber(loginPhone);
      setPhoneFromLogin(true);
      localStorage.removeItem('loginPhoneForRegistration');
      // Reset registration state for fresh start
      setOtpSent(false);
      setOtpVerified(false);
      setOtpTimer(0);
      localStorage.removeItem('registrationOtpSent');
      localStorage.removeItem('registrationOtpVerified');
      localStorage.removeItem('registrationOtpTimer');
      localStorage.removeItem('registrationOtpTimestamp');
      return;
    } else if (loginPhone && phoneNumber === loginPhone) {
      setPhoneFromLogin(true);
      localStorage.removeItem('loginPhoneForRegistration');
    }
    
    // If user came from login redirect but no phone number is saved, clear the redirect state
    if (!savedPhone && !phoneNumber && !loginPhone) {
      // Reset to phone input step
      setOtpSent(false);
      setOtpVerified(false);
      setOtpTimer(0);
      localStorage.removeItem('registrationOtpSent');
      localStorage.removeItem('registrationOtpVerified');
      localStorage.removeItem('registrationOtpTimer');
      localStorage.removeItem('registrationOtpTimestamp');
    } else if (savedPhone && !phoneNumber) {
      // Restore saved phone number
      setPhoneNumber(savedPhone);
    }
    
    // If there's a saved timer and it hasn't expired, continue the countdown
    if (savedOtpTimer > 0 && savedTimestamp > 0) {
      const elapsed = currentTime - savedTimestamp;
      const remainingTime = Math.max(0, savedOtpTimer - elapsed);
      
      if (remainingTime > 0) {
        setOtpTimer(remainingTime);
      } else {
        // Timer expired, clear the OTP sent state
        setOtpSent(false);
        setOtpTimer(0);
        localStorage.removeItem('registrationOtpSent');
        localStorage.removeItem('registrationOtpTimer');
        localStorage.removeItem('registrationOtpTimestamp');
      }
    }
  }, [phoneNumber]);

  // Save timestamp when OTP timer starts
  useEffect(() => {
    if (otpTimer > 0) {
      localStorage.setItem('registrationOtpTimestamp', Math.floor(Date.now() / 1000).toString());
    }
  }, [otpTimer]);

  // Clear registration state on successful registration
  const clearRegistrationState = () => {
    localStorage.removeItem('registrationPhone');
    localStorage.removeItem('registrationOtpSent');
    localStorage.removeItem('registrationOtpVerified');
    localStorage.removeItem('registrationOtpTimer');
    localStorage.removeItem('registrationOtpTimestamp');
    localStorage.removeItem('registrationFormData');
  };

  useEffect(() => {
    if (isAuthenticated) {
      clearRegistrationState();
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error || message) {
      const timer = setTimeout(() => {
        dispatch(clearError());
        dispatch(clearMessage());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, message, dispatch]);

  // Load countries on component mount
  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (formData.country) {
      const stateList = State.getStatesOfCountry(formData.country);
      setStates(stateList);
      setCities([]); // Clear cities when country changes
      setFormData(prev => ({ ...prev, state: '', city: '' }));
    }
  }, [formData.country]);

  // Load cities when state changes
  useEffect(() => {
    if (formData.country && formData.state) {
      const cityList = City.getCitiesOfState(formData.country, formData.state);
      setCities(cityList);
      setFormData(prev => ({ ...prev, city: '' }));
    }
  }, [formData.country, formData.state]);

  // Set default country based on phone number
  useEffect(() => {
    if (phoneNumber && phoneNumber.startsWith('+91')) {
      setFormData(prev => ({ ...prev, country: 'IN' }));
    }
  }, [phoneNumber]);

  // OTP Timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => {
        setOtpTimer(otpTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [otpTimer]);

  // Validate phone number
  const validatePhone = (phone) => {
    if (!phone) {
      setPhoneError('Phone number is required');
      return false;
    }
    
    if (!isValidPhoneNumber(phone)) {
      setPhoneError('Please enter a valid phone number');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    
    if (isButtonDisabled || otpLoading) return;
    
    if (!validatePhone(phoneNumber)) return;
    
    const formattedPhone = formatPhoneForAPI(phoneNumber);
    console.log('Sending registration OTP to:', formattedPhone);
    
    setIsButtonDisabled(true);
    
    try {
      await dispatch(sendRegisterOtp({ phone: formattedPhone })).unwrap();
      setOtpSent(true);
      setOtpTimer(OTP_CONFIG.TIMER_SECONDS);
      setOtp('');
    } catch (error) {
      console.error('Failed to send OTP:', error);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, OTP_CONFIG.BUTTON_DISABLE_SECONDS * 1000);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    if (isLoading) return;
    
    if (!validatePhone(phoneNumber)) return;
    
    if (!otp || otp.length !== OTP_CONFIG.LENGTH) {
      alert('Please enter a valid 6-digit OTP');
      return;
    }
    
    const formattedPhone = formatPhoneForAPI(phoneNumber);
    
    try {
      // Use the new verifyOTPOnly mutation
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            mutation {
              verifyOTPOnly(phone: "${formattedPhone}", otp: "${otp}", purpose: "register") {
                success
                message
              }
            }
          `
        })
      });
      
      const result = await response.json();
      
      if (result.errors) {
        const errorMessage = result.errors[0].message;
        throw new Error(errorMessage);
      }
      
      const { success, message } = result.data.verifyOTPOnly;
      
      if (!success) {
        throw new Error(message || 'OTP verification failed');
      }
      
      // OTP verified successfully, proceed to registration form
      setOtpVerified(true);
      
    } catch (error) {
      console.error('OTP verification failed:', error);
      alert(error.message || 'Invalid OTP. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (isLoading) return;
    
    // Validate all required fields
    const requiredFields = ['firstName', 'lastName', 'email', 'ownerName', 'companyName', 'businessIndustry', 'city', 'state'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      alert(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    const formattedPhone = formatPhoneForAPI(phoneNumber);
    const selectedCountry = countries.find(c => c.isoCode === formData.country);
    
    const registrationData = {
      ...formData,
      phone: formattedPhone,
      country: selectedCountry?.name || 'India',
      otp
    };
    
    try {
      await dispatch(registerUser(registrationData)).unwrap();
      // Clear registration state on success
      clearRegistrationState();
    } catch (error) {
      console.error('Registration failed:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-bulky-navy-900 flex items-center justify-center p-6 font-sans relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-bulky-blue-500/10 rounded-full blur-[120px] -mr-40 -mt-40"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-bulky-navy-500/20 rounded-full blur-[100px] -ml-20 -mb-20"></div>

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative z-10 border border-white/10">
        <div className="p-8 pb-4 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white p-3 rounded-2xl shadow-xl shadow-bulky-blue-500/10 border border-gray-50 transform -rotate-3 hover:rotate-0 transition-transform duration-500">
              <img
                src="/bm-logo.png"
                alt="Bulky Marketing Logo"
                className="h-12 w-12 object-contain"
              />
            </div>
          </div>
          <h2 className="font-medium">
            {!otpSent ? 'Register' : !otpVerified ? 'Verify OTP' : 'Complete Profile'}
          </h2>
          <p className="text-bulky-blue-500 font-normal uppercase tracking-[0.3em] text-[10px] mb-4">
            {!otpSent ? 'Create New Account' : !otpVerified ? 'Check WhatsApp' : 'Getting Started'}
          </p>
          <div className="flex justify-center gap-1.5 mb-2">
            <div className={`h-1 w-8 rounded-full ${!otpSent ? 'bg-bulky-blue-500' : 'bg-bulky-blue-200'}`}></div>
            <div className={`h-1 w-8 rounded-full ${otpSent && !otpVerified ? 'bg-bulky-blue-500' : 'bg-bulky-blue-200'}`}></div>
            <div className={`h-1 w-8 rounded-full ${otpVerified ? 'bg-bulky-blue-500' : 'bg-bulky-blue-200'}`}></div>
          </div>
        </div>

        {/* Step 1: Phone Number */}
        {!otpSent && (
          <form onSubmit={handleSendOtp} className="px-6 pb-6">
            {/* Show info if phone number came from login */}
            {phoneFromLogin && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="material-icons-round text-blue-500 text-sm">info</span>
                  <p className="text-xs font-normal">Phone number from login attempt - verify to continue registration</p>
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <label htmlFor="phone" className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-2 ml-1">
                Mobile Number
              </label>
              <div className="relative">
                <PhoneInput
                  international
                  defaultCountry={DEFAULTS.COUNTRY}
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  className="phone-input w-full"
                  placeholder="+91 98765 43210"
                />
                {phoneError && (
                  <p className="text-red-500 text-xs mt-1 ml-1">{phoneError}</p>
                )}
              </div>
            </div>

            <button
              type="submit"
              className={`w-full bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal py-3 px-4 rounded-xl transition-all duration-300 shadow-xl shadow-bulky-blue-500/20 active:scale-95 flex items-center justify-center gap-2 text-sm mb-3 ${
                otpLoading || !phoneNumber || isButtonDisabled || !isValidPhoneNumber(phoneNumber || '') ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={otpLoading || !phoneNumber || isButtonDisabled || !isValidPhoneNumber(phoneNumber || '')}
            >
              {otpLoading ? (
                <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
              ) : (
                <>
                  <span className="material-icons-round">send</span>
                  <span>{isButtonDisabled ? 'Please wait...' : 'Send OTP'}</span>
                </>
              )}
            </button>

            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="material-icons-round text-blue-500 text-sm">info</span>
                <p className="text-xs font-normal">OTP via WhatsApp</p>
              </div>
              <p className="text-xs ml-6">Check your mobile device (not WhatsApp Web)</p>
              <p className="text-xs ml-6 mt-1 text-amber-600">⚠️ Your progress will be saved if you navigate away</p>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-bulky-blue-500 hover:text-bulky-blue-600 text-sm font-normal mr-4"
              >
                Already have an account? Login
              </button>
              {(otpSent || otpVerified) && (
                <button
                  type="button"
                  onClick={() => {
                    clearRegistrationState();
                    setPhoneNumber('+91');
                    setOtp('');
                    setOtpSent(false);
                    setOtpVerified(false);
                    setOtpTimer(0);
                    setFormData({
                      firstName: '',
                      lastName: '',
                      email: '',
                      ownerName: '',
                      companyName: '',
                      businessIndustry: '',
                      city: '',
                      state: '',
                      country: 'IN'
                    });
                  }}
                  className="text-red-500 hover:text-red-600 text-sm font-normal"
                >
                  Start Over
                </button>
              )}
            </div>
          </form>
        )}

        {/* Step 2: OTP Verification */}
        {otpSent && !otpVerified && (
          <form onSubmit={handleVerifyOtp} className="px-6 pb-6">
            <div className="mb-4">
              <label htmlFor="otp" className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-2 ml-1">
                OTP {otpTimer > 0 && <span className="text-bulky-blue-500">({formatTime(otpTimer)})</span>}
              </label>
              <div className="relative group">
                <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors text-sm">lock_open</span>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter 6-digit OTP"
                  maxLength={OTP_CONFIG.LENGTH}
                  required
                  className="w-full pl-10 pr-3 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all duration-300 font-normal text-bulky-navy-800 tracking-[0.2em] text-sm"
                />
              </div>
            </div>

            <button
              type="submit"
              className={`w-full bg-bulky-navy-500 hover:bg-bulky-blue-600 text-white font-normal py-3 px-4 rounded-xl transition-all duration-300 shadow-xl shadow-bulky-blue-500/20 active:scale-95 flex items-center justify-center gap-2 text-sm mb-3 ${
                isLoading || !otp || otp.length !== OTP_CONFIG.LENGTH ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={isLoading || !otp || otp.length !== OTP_CONFIG.LENGTH}
            >
              {isLoading ? (
                <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
              ) : (
                <>
                  <span>Verify OTP</span>
                  <span className="material-icons-round">arrow_forward</span>
                </>
              )}
            </button>

            <div className="mb-3 p-2 bg-green-50 border border-green-200 text-green-700 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="material-icons-round text-green-500 text-sm">sms</span>
                <p className="text-xs">Check your mobile WhatsApp • Expires in {formatTime(otpTimer)}</p>
              </div>
            </div>

            {otpTimer === 0 && (
              <button
                type="button"
                onClick={() => {
                  setOtpSent(false);
                  setOtp('');
                }}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white font-normal py-2 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm"
              >
                <span className="material-icons-round">refresh</span>
                <span>Send New OTP</span>
              </button>
            )}
          </form>
        )}

        {/* Step 3: Registration Form */}
        {otpVerified && (
          <form onSubmit={handleRegister} className="px-6 pb-6 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-1 ml-1">
                  First Name*
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border-2 border-transparent rounded-xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all duration-300 text-sm"
                  placeholder="First Name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-1 ml-1">
                  Last Name*
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border-2 border-transparent rounded-xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all duration-300 text-sm"
                  placeholder="Last Name"
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-1 ml-1">
                Email Address*
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border-2 border-transparent rounded-xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all duration-300 text-sm"
                placeholder="Email Address"
                required
              />
            </div>

            <div className="mb-3">
              <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-1 ml-1">
                Owner Name*
              </label>
              <input
                type="text"
                value={formData.ownerName}
                onChange={(e) => handleInputChange('ownerName', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border-2 border-transparent rounded-xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all duration-300 text-sm"
                placeholder="Owner Name"
                required
              />
            </div>

            <div className="mb-3">
              <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-1 ml-1">
                Company Name*
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border-2 border-transparent rounded-xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all duration-300 text-sm"
                placeholder="Company Name"
                required
              />
            </div>

            <div className="mb-3">
              <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-1 ml-1">
                Business Industry*
              </label>
              <input
                type="text"
                value={formData.businessIndustry}
                onChange={(e) => handleInputChange('businessIndustry', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border-2 border-transparent rounded-xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all duration-300 text-sm"
                placeholder="Business Industry"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-1 ml-1">
                  State*
                </label>
                <select
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border-2 border-transparent rounded-xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all duration-300 text-sm"
                  required
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.isoCode} value={state.isoCode}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-1 ml-1">
                  City*
                </label>
                <select
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border-2 border-transparent rounded-xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all duration-300 text-sm"
                  required
                  disabled={!formData.state}
                >
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              className={`w-full bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal py-3 px-4 rounded-xl transition-all duration-300 shadow-xl shadow-bulky-blue-500/20 active:scale-95 flex items-center justify-center gap-2 text-sm mb-3 ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
              ) : (
                <>
                  <span>Create Account</span>
                  <span className="material-icons-round">person_add</span>
                </>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-bulky-blue-500 hover:text-bulky-blue-600 text-sm font-normal"
              >
                Already have an account? Login
              </button>
            </div>
          </form>
        )}

        {error && (
          <div className={`mx-8 mb-6 p-3 border-l-4 rounded-lg flex items-center gap-2 ${
            error.includes('rate limit') || error.includes('Rate limit')
              ? 'bg-amber-50 border-amber-500 text-amber-700' 
              : 'bg-red-50 border-red-500 text-red-700'
          }`}>
            <span className={`material-icons-round text-sm ${
              error.includes('rate limit') || error.includes('Rate limit')
                ? 'text-amber-500'
                : 'text-red-500'
            }`}>
              {error.includes('rate limit') || error.includes('Rate limit')
                ? 'schedule'
                : 'error_outline'}
            </span>
            <div className="flex-1">
              <p className="text-xs font-normal">{error}</p>
              {(error.includes('rate limit') || error.includes('Rate limit')) && (
                <div className="mt-2">
                  <p className="text-xs">You've reached the maximum OTP requests for this hour.</p>
                  <p className="text-xs mt-1">Current limit: 3 OTP requests per hour per phone number</p>
                </div>
              )}
            </div>
          </div>
        )}

        {message && (
          <div className="mx-8 mb-6 p-3 bg-blue-50 border-l-4 border-bulky-blue-500 text-bulky-blue-700 rounded-lg flex items-center gap-2">
            <span className="material-icons-round text-bulky-blue-500 text-sm">check_circle_outline</span>
            <p className="text-xs font-normal">{message}</p>
          </div>
        )}

        <div className="bg-gray-50 p-4 text-center border-t border-gray-100">
          <p className="text-[9px] text-gray-400 font-normal uppercase tracking-wider mb-1">Secure Registration</p>
          <p className="text-xs text-bulky-navy-300 font-normal">Your data is protected with enterprise-grade security.</p>
        </div>
      </div>
    </div>
  );
};

export default Register;